import FHIRFetcher from 'data/FHIRFetcher';
import type { Bundle, BundleEntry, Patient } from 'fhir/r4';
import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ApiFetchError } from 'services/api-client';
import useSWR from 'swr';
import type { Fetched } from 'types';
import { normalizePhoneNumber, normalizePhoneNumberDashed, normalizePhoneNumberParenthesized } from 'utils';
import { authUtils } from 'utils/auth';

/**
 * A FHIR `Patient` flattened for the patient-search results list.
 *
 * Distinct from `types/patient-data`'s `FlatPatient`, which is built by
 * `utils/FHIRUtils.parseFHIRPatient` through FHIRPath and carries raw evaluate
 * arrays. This one is built by plain property access below, so its fields are
 * scalars — the two are not interchangeable despite the shared function name.
 */
export type SearchResultPatient = {
  /** `Patient.id`, which FHIR leaves `0..1`. */
  id: string | undefined;
  /** First given name and family joined by a space; `''` when neither is present. */
  name: string;
  firstName: string;
  lastName: string;
  /** Home phone if present, else mobile, else `''`. */
  phone: string;
  mobilePhone: string | undefined;
  email: string | undefined;
  dateOfBirth: string | undefined;
  gender: Patient['gender'];
  /** Address parts joined by `', '`; `''` when the patient has no address. */
  address: string;
  memberId: string | undefined;
  memberStatus: string | undefined;
  isActive: boolean;
};

/**
 * `Partial<Patient>` rather than `Patient`: every read below is optional-chained,
 * and `use-patient-search.spec` calls this with bare fragments such as
 * `{ id: 'pat-1' }` and `{ name: [{ family: 'Lovelace' }] }`. The parameter must
 * admit what its callers actually pass.
 */
export const parseFHIRPatient = (fhirPatient: Partial<Patient>): SearchResultPatient => {
  const nameObj = fhirPatient.name?.[0];
  const firstName = nameObj?.given?.[0] || '';
  const lastName = nameObj?.family || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  // Get phone numbers
  const homeTelecom = fhirPatient.telecom?.find((t) => t.system === 'phone' && t.use === 'home');
  const mobileTelecom = fhirPatient.telecom?.find((t) => t.system === 'phone' && t.use === 'mobile');
  const phone = homeTelecom?.value || mobileTelecom?.value || '';
  const mobilePhone = mobileTelecom?.value;

  const emailTelecom = fhirPatient.telecom?.find((t) => t.system === 'email');
  const email = emailTelecom?.value;

  const addressObj = fhirPatient.address?.[0];
  let addressString = '';
  if (addressObj) {
    const parts = [
      ...(addressObj.line || []),
      addressObj.city,
      addressObj.state,
      addressObj.postalCode,
      addressObj.country,
    ].filter(Boolean);
    addressString = parts.join(', ');
  }

  // Get member ID and status from identifiers
  const memberIdIdentifier = fhirPatient.identifier?.find(
    (i) => i.system === 'https://projectwell.io/fhir/identifiers/member-id'
  );
  const memberStatusIdentifier = fhirPatient.identifier?.find(
    (i) => i.system === 'https://projectwell.io/fhir/identifiers/member-status'
  );

  return {
    id: fhirPatient.id,
    name: fullName,
    firstName,
    lastName,
    phone,
    mobilePhone,
    email,
    dateOfBirth: fhirPatient.birthDate,
    gender: fhirPatient.gender,
    address: addressString,
    memberId: memberIdIdentifier?.value,
    memberStatus: memberStatusIdentifier?.value,
    isActive: fhirPatient.active ?? false,
  };
};

/**
 * Flattens a `/Patient/?…` bundle's `entry` array into the results list.
 *
 * The parameter is the bundle's `entry`, not the bundle — the one caller passes
 * `fhirData.entry`, and `use-patient-search.spec` passes an entry array
 * directly. `Object.values` on an array yields its elements, so the traversal is
 * the same as a `.filter` over the array itself; it is left as written.
 *
 * Entries with no `resource` are dropped, which is how a search-mode `outcome`
 * entry is excluded — the spec pins that.
 */
export const parseFHIRSearchResponse = (response: BundleEntry[]): SearchResultPatient[] => {
  return Object.values(response)
    .filter((entry) => !!entry.resource)
    .map((entry) => parseFHIRPatient(entry.resource as Partial<Patient>));
};

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

type SearchMode = 'lookup' | 'search';
type SearchType = 'phone' | 'email';

interface UsePatientSearchReturn {
  phone: string;
  email: string;
  searchType: 'phone' | 'email' | null;
  searchMode: SearchMode;
  activeSearchType: SearchType;
  autoRedirect: boolean;
  results: SearchResultPatient[];
  isLoading: boolean;
  error: string | null;
  updateSearchParams: (params: {
    phone?: string;
    email?: string;
    searchType?: SearchType;
    shouldRedirect?: boolean;
  }) => void;
  clearSearch: () => void;
  /** The raw bundle entries, for consumers that need `entry.resource.id`. */
  unparsedData: BundleEntry[];
  searchValue: string;
}

export function usePatientSearch(): UsePatientSearchReturn {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = authUtils.getToken();

  const phone = searchParams.get('phone') ? decodeURIComponent(searchParams.get('phone')!) : '';
  const email = searchParams.get('email') ? decodeURIComponent(searchParams.get('email')!) : '';
  const rawType = searchParams.get('type');
  const redirect = searchParams.get('redirect');

  const activeSearchType: SearchType = rawType === 'email' || rawType === 'phone' ? rawType : 'phone';

  const autoRedirect = redirect === 'true';
  const searchMode: SearchMode = autoRedirect ? 'lookup' : 'search';

  // Build the search configuration
  const normalizedPhone = normalizePhoneNumber(phone);
  const dashedPhone = normalizePhoneNumberDashed(normalizedPhone);
  const parenthesizedPhone = normalizePhoneNumberParenthesized(normalizedPhone);
  const trimmedEmail = email.trim().toLowerCase();

  const isValidPhone = normalizedPhone.length === 10 && !normalizedPhone.startsWith('1');
  const isValidEmailFormat = isValidEmail(trimmedEmail);

  let searchType: 'phone' | 'email' | null = null;
  let searchValue = '';
  let queryParam = '';
  let shouldFetch = false;

  if (phone && isValidPhone) {
    searchType = 'phone';
    searchValue = normalizedPhone;
    queryParam = `phone=${encodeURIComponent(normalizedPhone)},${encodeURIComponent(dashedPhone)},${encodeURIComponent(parenthesizedPhone)}`;
    shouldFetch = true;
  } else if (email && isValidEmailFormat) {
    searchType = 'email';
    searchValue = trimmedEmail;
    queryParam = `email=${encodeURIComponent(trimmedEmail)}`;
    shouldFetch = true;
  }

  const swrKey = shouldFetch && token ? `/Patient/?${queryParam}` : null;

  const {
    data: fhirData,
    error,
    isValidating: isLoading,
    // `token` is `string | null`, but `swrKey` above is `null` unless `token` is
    // truthy and SWR does not call the fetcher for a null key — so the fetcher
    // only ever runs with a real token. The `!` states that gating rather than
    // introducing a fallback token.
  } = useSWR<Fetched<Bundle>, ApiFetchError>(swrKey, (url: string) => FHIRFetcher(url, token!), {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    dedupingInterval: 2000,
  });

  // Parse FHIR data to Patient array
  const results = useMemo(() => {
    if (!fhirData) return [];
    // `entry` is `0..*` in FHIR, so a bundle carrying no match has none. The
    // assertion preserves the existing throw: `Object.values(undefined)` fails
    // today, and defaulting to `[]` would render "0 patients found" for a
    // malformed response instead.
    return parseFHIRSearchResponse(fhirData.entry as BundleEntry[]);
  }, [fhirData]);

  const unparsedResults = useMemo(() => {
    return fhirData?.entry || [];
  }, [fhirData]);

  useEffect(() => {
    if (autoRedirect && results.length === 1 && !isLoading) {
      navigate(`/patients/${results[0]!.id}`, { replace: true });
    }
  }, [autoRedirect, results, isLoading, navigate]);

  useEffect(() => {
    const hasAnyParams = searchParams.toString().length > 0;

    if (!hasAnyParams) {
      const defaultParams: Record<string, string> = {
        type: 'phone',
        redirect: 'false',
      };
      setSearchParams(defaultParams, { replace: true });
    }
  }, []);

  const updateSearchParams = ({
    phone: newPhone,
    email: newEmail,
    searchType: newSearchType,
    shouldRedirect,
  }: {
    phone?: string;
    email?: string;
    searchType?: SearchType;
    shouldRedirect?: boolean;
  }): void => {
    const params: Record<string, string> = {};

    const effectiveSearchType = newSearchType || activeSearchType;

    // Only set the search value for the active search type
    if (effectiveSearchType === 'phone') {
      if (newPhone !== undefined) {
        if (newPhone) params['phone'] = encodeURIComponent(newPhone);
      } else if (phone) {
        params['phone'] = encodeURIComponent(phone);
      }
    } else {
      if (newEmail !== undefined) {
        if (newEmail) params['email'] = encodeURIComponent(newEmail);
      } else if (email) {
        params['email'] = encodeURIComponent(email);
      }
    }

    params['type'] = encodeURIComponent(effectiveSearchType);

    // Maintains the current redirect state unless the caller overrides it.
    // Set redirect based on shouldRedirect or maintain current state
    const effectiveRedirect = shouldRedirect !== undefined ? shouldRedirect : autoRedirect;
    params['redirect'] = String(effectiveRedirect);

    setSearchParams(params);
  };

  const clearSearch = (): void => {
    // Keep type and redirect, just clear the search values
    const params: Record<string, string> = {
      type: encodeURIComponent(activeSearchType),
      redirect: String(autoRedirect),
    };
    setSearchParams(params);
  };

  return {
    phone,
    email,
    searchType,
    searchMode,
    activeSearchType,
    autoRedirect,
    results,
    isLoading,
    // `.message` is `undefined` on `ApiFetchError`'s string branch, surfacing as a
    // blank reason. KNOWN_BUGS.md HD-2.
    error: (error as { message?: string })?.message || null,
    updateSearchParams,
    clearSearch,
    unparsedData: unparsedResults,
    searchValue,
  };
}
